/**
 * Coquille applicative : barre laterale claire sur grand ecran, barre
 * superieure avec la recherche globale, tiroir de navigation sous 1024 px.
 * Le texte juridique de l'ancien pied de page vit desormais dans
 * Parametres > Integrations.
 */
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronsUpDown, LogOut, Menu, UserRound } from 'lucide-react';
import { LIBELLES_ROLE } from '@leads/shared';
import { useAuth } from '../lib/auth';
import { useRequeteMedia } from '../lib/hooks';
import { useNotificationsProspection } from '../lib/notifications';
import { usePermissions } from '../lib/permissions';
import { NOM_APPLICATION, SIGLE_APPLICATION } from '../lib/marque';
import {
  Avatar,
  BoutonIcone,
  CLASSES_SOMMAIRE,
  ElementMenu,
  MenuDeroulant,
  PanneauLateral,
  SeparateurMenu,
} from '../composants/ui';
import { Autorise } from '../composants/Autorisation';
import { BoutonNouveauLead } from '../composants/NouveauLead';
import { RechercheGlobale } from '../composants/RechercheGlobale';
import { ListeNavigation, NAVIGATION, avecCompteurRelances, entreesAutorisees } from './navigation';

/**
 * Pastille et nom seuls : la baseline, plus large que la barre laterale,
 * y serait tronquee. `compacte` masque le nom sur les plus petits ecrans.
 */
function Marque({ compacte = false }: { compacte?: boolean }) {
  return (
    <Link to="/" aria-label={NOM_APPLICATION} className="flex min-w-0 items-center gap-2.5 rounded-md">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-600 text-xs font-bold text-encre-inverse shadow-pose"
      >
        {SIGLE_APPLICATION}
      </span>
      <span className={`truncate text-base font-semibold text-encre ${compacte ? 'hidden sm:block' : ''}`}>
        {NOM_APPLICATION}
      </span>
    </Link>
  );
}

/**
 * Compte connecte et son menu, aligne sur la largeur du declencheur. Il
 * s'ouvre vers le haut en pied de barre laterale ; dans le tiroir, ou le bloc
 * suit la liste, vers le bas plutot que par-dessus les liens.
 */
function BlocUtilisateur({ direction = 'haut' }: { direction?: 'haut' | 'bas' }) {
  const { utilisateur, deconnexion } = useAuth();
  const naviguer = useNavigate();
  if (!utilisateur) return null;

  return (
    <MenuDeroulant
      direction={direction}
      alignement="gauche"
      largeur="w-full"
      classeDeclencheur={`flex w-full items-center gap-2.5 rounded-md p-2 text-left hover:bg-nav-survol ${CLASSES_SOMMAIRE}`}
      declencheur={
        <>
          <Avatar nom={utilisateur.nom} identifiant={utilisateur.id} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-encre">{utilisateur.nom}</span>
            <span className="block truncate text-xs text-encre-discrete">{LIBELLES_ROLE[utilisateur.role]}</span>
          </span>
          <ChevronsUpDown aria-hidden="true" className="shrink-0 text-encre-tenue" />
        </>
      }
    >
      <p className="truncate px-2.5 py-1.5 text-xs text-encre-discrete">{utilisateur.email}</p>
      <ElementMenu icone={<UserRound aria-hidden="true" />} onClick={() => naviguer('/profil')}>
        Mon profil
      </ElementMenu>
      <SeparateurMenu />
      <ElementMenu icone={<LogOut aria-hidden="true" />} onClick={deconnexion}>
        Se déconnecter
      </ElementMenu>
    </MenuDeroulant>
  );
}

export function Layout() {
  const { role } = usePermissions();
  // Monte une fois par session : badge relu chaque minute, titre de l'onglet, message du jour.
  const nbRelances = useNotificationsProspection();
  const entrees = avecCompteurRelances(entreesAutorisees(NAVIGATION, role), nbRelances);
  const [menuOuvert, setMenuOuvert] = useState(false);
  const { pathname } = useLocation();

  // Toute navigation referme le tiroir, bouton Precedent du navigateur compris.
  useEffect(() => {
    setMenuOuvert(false);
  }, [pathname]);

  // Au-dela de 1024 px la barre laterale prend le relais : un tiroir reste
  // ouvert laisserait son voile sur la page et le defilement bloque.
  const grandEcran = useRequeteMedia('(min-width: 1024px)');
  useEffect(() => {
    if (grandEcran) setMenuOuvert(false);
  }, [grandEcran]);

  return (
    <div className="min-h-screen bg-fond lg:grid lg:grid-cols-[var(--largeur-navigation)_minmax(0,1fr)]">
      <a
        href="#contenu-principal"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-60 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:shadow-flottant"
      >
        Aller au contenu principal
      </a>

      <aside
        aria-label="Barre latérale"
        className="sticky top-0 hidden h-screen flex-col self-start border-r border-nav-bordure bg-nav-fond lg:flex"
      >
        <div className="flex h-(--hauteur-entete) shrink-0 items-center px-4">
          <Marque />
        </div>
        <nav aria-label="Navigation principale" className="flex-1 overflow-y-auto px-3 py-3">
          <ListeNavigation entrees={entrees} />
        </nav>
        <div className="border-t border-nav-bordure p-3">
          <BlocUtilisateur />
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-(--hauteur-entete) items-center gap-3 border-b border-bordure bg-surface/90 px-4 backdrop-blur sm:px-6 lg:px-8">
          <BoutonIcone
            libelle="Ouvrir le menu"
            icone={Menu}
            taille="md"
            className="lg:hidden"
            aria-expanded={menuOuvert}
            onClick={() => setMenuOuvert(true)}
          />
          <div className="lg:hidden">
            <Marque compacte />
          </div>
          <div className="ml-auto w-full min-w-0 max-w-md lg:ml-0 lg:max-w-xl">
            <RechercheGlobale />
          </div>
          <Autorise permission="leads.creer">
            <div className="shrink-0 lg:ml-auto"><BoutonNouveauLead /></div>
          </Autorise>
        </header>

        {/* Cible du lien d'evitement, pas un controle : pas d'anneau de focus autour de toute la page. */}
        <main id="contenu-principal" tabIndex={-1} className="min-w-0 flex-1 p-4 focus:outline-none sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <PanneauLateral
        cote="gauche"
        largeur="max-w-xs"
        titre="Menu"
        ouvert={menuOuvert}
        onFermer={() => setMenuOuvert(false)}
      >
        {/* Un lien choisi est une intention accomplie, meme vers la page deja
            affichee : son adresse ne change pas, l'effet sur `pathname` ne le verrait pas. */}
        <nav
          aria-label="Navigation principale"
          onClick={(evenement) => {
            if (evenement.target instanceof Element && evenement.target.closest('a')) setMenuOuvert(false);
          }}
        >
          <ListeNavigation entrees={entrees} />
        </nav>
        <div className="mt-6 border-t border-nav-bordure pt-3">
          <BlocUtilisateur direction="bas" />
        </div>
      </PanneauLateral>
    </div>
  );
}
